import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ClipboardList } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  coachCountColumn,
  createdColumn,
  DescriptionField,
  DRAFT_PENDING_LINK_CLASS,
  previewColumn,
  ROW_LINK_CLASS,
  statusColumn,
  TitleSlugFields,
  useAdminListCreateState,
} from "~/components/cih/admin-list-page";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { ccoDropdownQueryOptions } from "~/lib/functions/admin-ctps";
import {
  createCihCtpFn,
  ctpQueryKeys,
  ctpsQueryOptions,
  type CihCtpListRow,
} from "~/lib/functions/cih/ctps";
import {
  COACH_COUNT_SORTS,
  type CoachCountSort,
  coachCountSearchSchema,
  nextCoachCountSortSearch,
  resolveCoachCountSort,
  sortByCoachCount,
} from "~/lib/shared/cih-coach-count-sort";

export const Route = createFileRoute("/admin/cih/ctps/")({
  staticData: { title: "Training Programs" },
  validateSearch: coachCountSearchSchema,
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(ctpsQueryOptions()),
      context.queryClient.ensureQueryData(ccoDropdownQueryOptions),
    ]);
    return {};
  },
  component: CtpsPage,
});

interface CreateFormState {
  name: string;
  slug: string;
  ccoId: string;
  description: string;
}

const EMPTY_FORM: CreateFormState = {
  name: "",
  slug: "",
  ccoId: "",
  description: "",
};

const CTP_COLUMNS: ColumnDef<CihCtpListRow>[] = [
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => (
      <Link
        to="/admin/cih/ctps/$ctpId"
        params={{ ctpId: row.original.id }}
        className={ROW_LINK_CLASS}
      >
        {row.original.name}
      </Link>
    ),
  },
  statusColumn(
    (row) =>
      row.pendingDraftId && (
        <Link
          to="/admin/cih/ctps/$ctpId"
          params={{ ctpId: row.pendingDraftId }}
          className={DRAFT_PENDING_LINK_CLASS}
        >
          {row.pendingDraftStatus === "in_review"
            ? "Waiting for review"
            : "Draft pending"}
        </Link>
      ),
  ),
  coachCountColumn(),
  createdColumn(),
  previewColumn((row) =>
    row.status === "published" ? `/training-programs/${row.slug}` : null,
  ),
];

function CtpsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const { sort, direction } = resolveCoachCountSort(Route.useSearch());
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const { create, form, setFormField } = useAdminListCreateState(EMPTY_FORM);

  const { data: ctps, isLoading } = useQuery(
    ctpsQueryOptions(statusFilter || undefined),
  );

  const { data: ccoOptions } = useQuery(ccoDropdownQueryOptions);

  const createMutation = useMutation({
    mutationFn: createCihCtpFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ctpQueryKeys.all });
      create.finish("Training program created.");
      navigate({
        to: "/admin/cih/ctps/$ctpId",
        params: { ctpId: created.id },
      });
    },
  });

  const handleSubmit = (): void => {
    if (!form.name.trim() || !form.slug.trim() || !form.ccoId) return;
    createMutation.mutate({
      data: {
        name: form.name.trim(),
        slug: form.slug.trim(),
        ccoId: form.ccoId,
        description: form.description.trim() || undefined,
      },
    });
  };

  const shownCtps = useMemo((): CihCtpListRow[] => {
    if (!ctps) return [];
    const q = search.toLowerCase();
    const matches = q
      ? ctps.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            (c.slug ?? "").toLowerCase().includes(q),
        )
      : ctps;
    return sortByCoachCount(matches, sort, direction);
  }, [ctps, search, sort, direction]);

  const changeSort = (column: CoachCountSort): void => {
    navigate({ search: nextCoachCountSortSearch({ sort, direction }, column) });
  };

  const createFields = (
    <>
      <TitleSlugFields
        create={create}
        idPrefix="ctp"
        titleLabel="Name"
        title={form.name}
        slug={form.slug}
        titlePlaceholder="e.g., ICF Level 2 Certification"
        slugPlaceholder="e.g., icf-level-2"
        slugRequired
        onTitleChange={(val) => setFormField("name", val)}
        onSlugChange={(val) => setFormField("slug", val)}
      />
      <div>
        <Label htmlFor="ctp-cco" className="mb-1">
          Parent CCO
        </Label>
        <Select
          value={form.ccoId}
          onValueChange={(val) => setFormField("ccoId", val)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a CCO..." />
          </SelectTrigger>
          <SelectContent>
            {ccoOptions?.map((cco) => (
              <SelectItem key={cco.id} value={cco.id}>
                {cco.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DescriptionField
        id="ctp-description"
        value={form.description}
        placeholder="Brief description of this training program..."
        optional
        onChange={(val) => setFormField("description", val)}
      />
    </>
  );

  return (
    <AdminListPage
      title="Training Programs"
      description="CTP content pages — manage program details and rich content"
      itemName="Training Program"
      create={create}
      createFields={createFields}
      onCreateSubmit={handleSubmit}
      canCreate={Boolean(form.name.trim() && form.slug.trim() && form.ccoId)}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      statusFilter={{ value: statusFilter, onChange: setStatusFilter }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search training programs...",
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownCtps}
        columns={CTP_COLUMNS}
        sorts={COACH_COUNT_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(search),
          icon: ClipboardList,
          itemName: "Training Program",
          title: "No training programs yet",
          description: "Create a training program to get started.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}
